import { createContext, useEffect, useContext, useCallback, useReducer, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthContext } from '../auth/useAuthContext';

// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';

// ----------------------------------------------------------------------

const initialState = {
  campaign_templates: [],
  template_feilds: [],
  page: 0,
  search: '',
  totalPages: 1,
  count: 0,
  error: false,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      ...state,
      campaign_templates: action.payload.campaign_templates,
      page: action.payload.page,
      totalPages: action.payload.totalPages,
      count: action.payload._count,
    };
  }
  if (action.type === 'FEILDS') {
    return {
      ...state,
      template_feilds: action?.payload?.template_feilds,
    };
  }
  if (action.type === 'ERROR') {
    return {
      ...state,
      error: true,
    };
  }
  if (action.type === 'PAGE_CHANGE') {
    return {
      ...state,
      page: action.payload.page,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

export const CampaignTemplateContext = createContext(initialState);

export const useCampaignTemplateContext = () => {
  const context = useContext(CampaignTemplateContext);

  if (!context)
    throw new Error('useCampaignTemplateContext must be use inside CampaignTemplateProvider');

  return context;
};

export function CampaignTemplateProvider() {
  const { user, logout } = useAuthContext();
  const storageAvailable = localStorageAvailable();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getCampaignTemplates = useCallback(
    async (vendor_id, search, page = 0, page_size) => {
      try {
        const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
        const config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: '/service/campaign_service/v1/campaign/templates/list',
          headers: {
            authorization: `Bearer ${accessToken}`,
            user,
            'Content-Type': 'application/json',
          },
          params: { vendor_id, search, page, page_size },
        };
        const response = await axios.request(config);
        const { _payload, currentPage, totalPages } = response.data;
        dispatch({
          type: 'INITIAL',
          payload: {
            campaign_templates: _payload,
            page: currentPage,
            totalPages,
          },
        });
      } catch (error) {
        dispatch({
          type: 'ERROR',
        });
        if (error === 'Unauthorized request') {
          logout();
        }
      }
    },
    [storageAvailable, user, logout]
  );

  useEffect(() => {
    getCampaignTemplates();
  }, [getCampaignTemplates]);

  
  const pageChange = useCallback(
    (page,vendor_id = '') => {
      getCampaignTemplates(vendor_id,"",page);
    },
    [getCampaignTemplates]
  );
  
  const searchTemplate = useCallback(
    (search, vendor_id = '') => {
      getCampaignTemplates(vendor_id, search);
    },
    [getCampaignTemplates]
  );
  const filterByVendor = useCallback(
    (vendor_id) => {
      getCampaignTemplates(vendor_id);
    },
    [getCampaignTemplates]
  );
  const getTemplateFeilds = useCallback(
    async (pagination = false) => {
      try {
        const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
        const config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: '/service/campaign_service/v1/campaign/template/fields/list',
          headers: {
            authorization: `Bearer ${accessToken}`,
            user,
            'Content-Type': 'application/json',
          },
          params: { pagination },
        };
        const response = await axios.request(config);
        const { _payload } = response.data;
        dispatch({
          type: 'FEILDS',
          payload: {
            template_feilds: _payload,
          },
        });
      } catch (error) {
        dispatch({
          type: 'ERROR',
        });
        if (error === 'Unauthorized request') {
          logout();
        }
      }
    },

    [storageAvailable, user, logout]
  );
  useEffect(() => {
    getTemplateFeilds();
  }, [getTemplateFeilds]);
  const createCampaignTemplate = useCallback(
    async (data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: '/service/campaign_service/v1/campaign/template/add',
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      await axios.request(config);
      getCampaignTemplates();
    },
    [getCampaignTemplates, storageAvailable, user]
  );

  const updateCampaignPackages = useCallback(
    async (id, data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `/service/accounts_service/v1/credits/package/${id}`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      await axios.request(config);
      getCampaignTemplates();
    },
    [getCampaignTemplates, storageAvailable, user]
  );

  const getVendorBankInfo = useCallback(
    async (id) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: `/system/partners/${id}/bankacc`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
      };
      const response = await axios.request(config);
      getCampaignTemplates();
      return response.data;
    },
    [getCampaignTemplates, storageAvailable, user]
  );

  const memoizedValue = useMemo(
    () => ({
      campaign_templates: state.campaign_templates,
      page: state.page,
      search: state.search,
      totalPages: state.totalPages,
      template_feilds: state.template_feilds,
      createCampaignTemplate,
      updateCampaignPackages,
      getVendorBankInfo,
      pageChange,
      searchTemplate,
      filterByVendor,
      getCampaignTemplates,
      getTemplateFeilds,
    }),
    [
      state.campaign_templates,
      state.template_feilds,
      state.page,
      state.totalPages,
      state.search,
      createCampaignTemplate,
      updateCampaignPackages,
      getVendorBankInfo,
      pageChange,
      searchTemplate,
      filterByVendor,
      getCampaignTemplates,
      getTemplateFeilds,
    ]
  );

  return (
    <CampaignTemplateContext.Provider value={memoizedValue}>
      <Outlet />
    </CampaignTemplateContext.Provider>
  );
}
